import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { FeedsPanel } from "@/features/feeds";
import { apiGet } from "@/lib/api-client";
import { asPercent, formatRelativeTime } from "@/lib/format";
import { asRecord, asArray } from "@/lib/type-utils";

/**
 * Backend sentiment is numeric (-1..1, ±0.1 neutral band — backend/routers/news.py).
 * Non-numeric payloads fall back to string matching so label-shaped feeds keep working.
 */
function classifySentiment(raw: unknown): string {
  if (raw === null || raw === undefined) return "neutral";
  const numeric =
    typeof raw === "number"
      ? raw
      : typeof raw === "string" && raw.trim() !== ""
        ? Number(raw)
        : NaN;
  if (Number.isFinite(numeric)) {
    return numeric > 0.1 ? "positive" : numeric < -0.1 ? "negative" : "neutral";
  }
  return String(raw).toLowerCase();
}

function articleSummary(article: Record<string, unknown>): string {
  return String(article.snippet ?? article.description ?? article.content ?? "");
}

export function NewsCenterPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sentimentFilter, setSentimentFilter] = useState<"all" | "positive" | "negative" | "neutral">("all");

  const newsQuery = useQuery({
    queryKey: ["news-center", "news"],
    queryFn: () => apiGet<unknown>("/api/news/latest?limit=50"),
    refetchInterval: 60_000,
  });

  const gdeltQuery = useQuery({
    queryKey: ["news-center", "gdelt"],
    queryFn: () => apiGet<unknown>("/api/news/gdelt?limit=20"),
    refetchInterval: 120_000,
  });

  const sentimentQuery = useQuery({
    queryKey: ["news-center", "sentiment"],
    queryFn: () => apiGet<unknown>("/api/news/sentiment"),
    refetchInterval: 300_000,
  });

  const news = asRecord(newsQuery.data);
  const articles = asArray(news.articles ?? news.items ?? news.news);
  const gdeltData = asRecord(gdeltQuery.data);
  const gdeltEvents = asArray(gdeltData.events ?? gdeltData.articles);
  const sentiment = asRecord(sentimentQuery.data);

  // article_count === 0 means the backend scored nothing — its neutral-100
  // fallback is a placeholder, not a measurement. Absent/errored reads are
  // never presented as a measured NEUTRAL either.
  const sentimentMeasured =
    !sentimentQuery.isError &&
    sentimentQuery.data !== undefined &&
    sentiment.article_count !== 0;
  const positiveSentiment = Number(sentiment.positive ?? sentiment.bullish ?? 0);
  const negativeSentiment = Number(sentiment.negative ?? sentiment.bearish ?? 0);
  const neutralSentiment = Number(sentiment.neutral ?? 0);
  const overallSentiment = String(sentiment.overall ?? sentiment.sentiment ?? (positiveSentiment > negativeSentiment ? "positive" : negativeSentiment > positiveSentiment ? "negative" : "neutral")).toLowerCase();

  const filteredArticles = articles.filter((article) => {
    if (sentimentFilter !== "all") {
      const cls = classifySentiment(article.sentiment);
      if (sentimentFilter === "positive" && !cls.includes("positive") && !cls.includes("bullish")) return false;
      if (sentimentFilter === "negative" && !cls.includes("negative") && !cls.includes("bearish")) return false;
      if (sentimentFilter === "neutral" && cls !== "neutral") return false;
    }
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const title = String(article.title ?? "").toLowerCase();
      const content = articleSummary(article).toLowerCase();
      return title.includes(search) || content.includes(search);
    }
    return true;
  });

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel
        title="News Center"
        description="Market-moving news aggregated from GDELT, Reuters, Bloomberg, and other sources."
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard
            label="Total Articles"
            value={newsQuery.isError ? "—" : String(articles.length)}
            hint="Latest news items"
          />
          <StatCard
            label="GDELT Events"
            value={gdeltQuery.isError ? "—" : String(gdeltEvents.length)}
            hint="Global event database"
          />
          <StatCard
            label="Overall Sentiment"
            value={sentimentQuery.isError ? "UNKNOWN" : sentimentMeasured ? String(overallSentiment).toUpperCase() : "NO DATA"}
            tone={sentimentMeasured && (overallSentiment === "positive" || overallSentiment === "bullish") ? "success" : sentimentMeasured && (overallSentiment === "negative" || overallSentiment === "bearish") ? "warning" : "default"}
            hint="Aggregate news sentiment"
          />
          <StatCard
            label="Positive %"
            value={sentimentMeasured ? asPercent(positiveSentiment / 100) : "n/a"}
            tone={sentimentMeasured && positiveSentiment > 50 ? "success" : "default"}
            hint="Bullish article ratio"
          />
          <StatCard
            label="Negative %"
            value={sentimentMeasured ? asPercent(negativeSentiment / 100) : "n/a"}
            tone={sentimentMeasured && negativeSentiment > 50 ? "warning" : "default"}
            hint="Bearish article ratio"
          />
        </div>
      </Panel>

      {/* Rich list (left, 2/3) + compact FeedsPanel headline column (right). */}
      <div className="grid items-start gap-3 md:gap-4 grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel
            title="News Feed"
            description="Latest market-moving articles"
            actions={
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <Input
                  type="text"
                  placeholder="Search news..."
                  className="text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Select
                  className="text-sm"
                  value={sentimentFilter}
                  onChange={(e) => setSentimentFilter(e.target.value as typeof sentimentFilter)}
                >
                  <option value="all">All Sentiment</option>
                  <option value="positive">Positive</option>
                  <option value="negative">Negative</option>
                  <option value="neutral">Neutral</option>
                </Select>
              </div>
            }
          >
            <div className="max-h-[500px] space-y-3 overflow-y-auto">
              {newsQuery.isError ? (
                <QueryErrorState what="news feed" onRetry={() => void newsQuery.refetch()} />
              ) : filteredArticles.length === 0 ? (
                <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                  No articles found
                </div>
              ) : (
                filteredArticles.slice(0, 20).map((article, index) => {
                  const cls = classifySentiment(article.sentiment);
                  const isPositive = cls.includes("positive") || cls.includes("bullish");
                  const isNegative = cls.includes("negative") || cls.includes("bearish");
                  const summary = articleSummary(article);

                  return (
                    <div key={index} className="rounded-lg bg-gunmetal/50 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-sm font-medium leading-tight">{String(article.title || "Untitled")}</h3>
                          {summary ? (
                            <p className="mt-1 text-xs text-slate-400 line-clamp-2">
                              {summary.slice(0, 200)}
                              {summary.length > 200 ? "..." : ""}
                            </p>
                          ) : null}
                          <div className="mt-2 flex items-center gap-3 text-xs">
                            <span className="text-slate-500">{String(article.source || article.publisher || "Unknown")}</span>
                            <span className="text-slate-600">•</span>
                            <span className="text-slate-500">{formatRelativeTime(String(article.published_at ?? article.date ?? article.timestamp ?? ""))}</span>
                            {article.market != null && (
                              <>
                                <span className="text-slate-600">•</span>
                                <span className="text-gold-400">{String(article.market)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-1 text-xs ${
                            isPositive ? "bg-green-500/20 text-green-400" : isNegative ? "bg-red-500/20 text-red-400" : "bg-slate-500/20 text-slate-400"
                          }`}
                        >
                          {isPositive ? "Bullish" : isNegative ? "Bearish" : "Neutral"}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Panel>
        </div>
        <FeedsPanel />
      </div>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Sentiment Distribution" description="News sentiment breakdown">
          {sentimentQuery.isError ? (
            <QueryErrorState what="sentiment data" onRetry={() => void sentimentQuery.refetch()} />
          ) : !sentimentMeasured ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              No sentiment data — no scored articles in the window
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-400">Positive</span>
                  <span>{positiveSentiment.toFixed(1)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gunmetal">
                  <div className="h-full rounded-full bg-green-500" style={{ width: `${positiveSentiment}%` }} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Neutral</span>
                  <span>{neutralSentiment.toFixed(1)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gunmetal">
                  <div className="h-full rounded-full bg-slate-500" style={{ width: `${neutralSentiment}%` }} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-red-400">Negative</span>
                  <span>{negativeSentiment.toFixed(1)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gunmetal">
                  <div className="h-full rounded-full bg-red-500" style={{ width: `${negativeSentiment}%` }} />
                </div>
              </div>
            </div>
          )}
        </Panel>

        <Panel title="GDELT Events" description="Global Database of Events, Language, and Tone">
          <div className="max-h-[300px] space-y-2 overflow-y-auto">
            {gdeltQuery.isError ? (
              <QueryErrorState what="GDELT events" onRetry={() => void gdeltQuery.refetch()} />
            ) : gdeltEvents.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No GDELT events available
              </div>
            ) : (
              gdeltEvents.slice(0, 10).map((event, index) => (
                <div key={index} className="rounded-lg bg-gunmetal/50 p-3">
                  <div className="text-sm font-medium">{String(event.title || event.event || "Unknown Event")}</div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                    <span>{String(event.country || event.location || "Global")}</span>
                    <span>•</span>
                    <span>{formatRelativeTime(event.date || event.timestamp)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Panel>
      </div>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="News API" description="Raw news data">
          <JsonView value={newsQuery.data} />
        </Panel>
        <Panel title="Sentiment API" description="Raw sentiment data">
          <JsonView value={sentimentQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
